import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'
import { contractsTableParams, type ContractsTableRequest, type TablePage } from './settingsContracts'

/**
 * Settings -> Contract Templates (`settings/contracts_templates`), calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Contracts_templates.php`:
 *
 *   index                        -> GET  /settings/contracts_templates
 *   get_data_contracts_templates -> GET  /settings/contracts_templates/list
 *   edit/{id}                    -> GET  /settings/contracts_templates/{id}
 *   send/create                  -> POST /settings/contracts_templates
 *   send/{id}                    -> POST /settings/contracts_templates/{id}
 *   disable                      -> POST /settings/contracts_templates/{id}/disable
 *   get_data_services_json/{id}  -> GET  /settings/contracts_templates/{id}/services
 *   assign_service/{id}          -> POST /settings/contracts_templates/{id}/services
 *
 * Plus doua capete de care are nevoie formularul de contract din ecranul
 * Contracts: `/select` (sabloanele aprinse) si `/{id}/contract_services`
 * (serviciile sablonului, cu cantitatile contractului cand e dat unul).
 */

export type ContractTypeOption = { ID: number; name: string }

export type ContractTemplatesShell = {
  pageTitle: string
  moduleUrl: string
  contractTypes: ContractTypeOption[]
}

export const contractTemplatesShellQuery = queryOptions({
  queryKey: ['settings', 'contracts_templates', 'shell'],
  queryFn: () => apiGet<ContractTemplatesShell>('/settings/contracts_templates'),
})

export type ContractTemplateRow = {
  ID: number
  name: string
  description: string
  /** "1" / "0" - macheta veche compara cu siruri. */
  status: string
  contract_type: number
  count_services: number
  DT_RowId?: string
}

export function contractTemplatesQuery(r: ContractsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contracts_templates', 'list', r],
    queryFn: () =>
      apiGet<TablePage<ContractTemplateRow>>(
        `/settings/contracts_templates/list?${contractsTableParams(r).toString()}`,
      ),
  })
}

export type ContractTemplate = {
  ID: number
  name: string
  description: string
  status: number
  contract_type: number
}

export function contractTemplateQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'contracts_templates', 'one', id],
    queryFn: () => apiGet<ContractTemplate>(`/settings/contracts_templates/${id}`),
  })
}

/** `id` 0 = creare (`send/create`), altfel schimbare (`send/{id}`). */
export function saveContractTemplate(id: number, fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number }>(
    id > 0 ? `/settings/contracts_templates/${id}` : '/settings/contracts_templates',
    fields,
  )
}

export function disableContractTemplate(id: number) {
  return apiPost<{ saved: boolean }>(`/settings/contracts_templates/${id}/disable`, {})
}

/** Un serviciu din lista de sub sablon, cu bifa "Assign". */
export type TemplateServiceRow = {
  service_id: number
  service_name: string
  service_category: string
  priority_name: string
  coverage_name: string
  sla_name: string
  resolve_sla_name: string
  intervention_sla_name: string
  UM_name: string
  has_quantity: number
  quantity: string
  cost: string
  currency: string
  assigned: boolean
  DT_RowId?: string
}

/** `templateId` 0 = fereastra de creare: aceeasi lista, cu bifele stinse. */
export function templateServicesQuery(templateId: number, r: ContractsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contracts_templates', 'services', templateId, r],
    queryFn: () =>
      apiGet<TablePage<TemplateServiceRow>>(
        `/settings/contracts_templates/${templateId}/services?${contractsTableParams(r).toString()}`,
      ),
  })
}

export function assignTemplateService(templateId: number, serviceId: number, on: boolean) {
  return apiPost<{ saved: boolean; assigned: boolean }>(`/settings/contracts_templates/${templateId}/services`, {
    service_id: serviceId,
    service_state: on ? 'on' : 'off',
  })
}

/* --- cele doua capete cerute de formularul de contract --- */

export type TemplateChoice = {
  ID: number
  name: string
  contract_type: number
  contract_type_name: string
}

export const contractTemplateChoicesQuery = queryOptions({
  queryKey: ['settings', 'contracts_templates', 'select'],
  queryFn: () => apiGet<{ rows: TemplateChoice[] }>('/settings/contracts_templates/select'),
})

/** Un rand din tabelul de servicii al formularului de contract. */
export type ContractFormServiceRow = {
  service_id: number
  service_name: string
  service_category: string
  priority_name: string
  coverage_name: string
  sla_name: string
  resolve_sla_name: string
  intervention_sla_name: string
  UM_name: string
  has_quantity: number
  quantity: string
  cost: string
  sell_cost: string
  currency: string
  DT_RowId?: string
}

export function contractFormServicesQuery(templateId: number, contractId: number, r: ContractsTableRequest) {
  const params = contractsTableParams(r)
  params.set('contract', String(contractId))
  return queryOptions({
    queryKey: ['settings', 'contracts_templates', 'contract_services', templateId, contractId, r],
    queryFn: () =>
      apiGet<TablePage<ContractFormServiceRow>>(
        `/settings/contracts_templates/${templateId}/contract_services?${params.toString()}`,
      ),
    enabled: templateId > 0,
  })
}
